'use client'

/**
 * Per-ticker community feed v2 — the live "hangout" stream on /stocks/[symbol]
 * (owner 2026-07-07: StockTwits-inspired, our own take).
 *
 * · Initial posts arrive via SSR props (crawlable HTML for the SEO play).
 * · LIVE: polls every 12s; new posts slide in with an amber ring so watchers see the
 *   conversation move without reloading. Chat cashtag mirrors keep their "live chat" chip.
 * · Composer: avatar chip, Enter-to-post (Shift+Enter = newline), gradient send button.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import { isAdminSession } from '@/lib/auth/admin'
import Link from 'next/link'
import { Eye, Radio, Send } from 'lucide-react'
import UserAvatar from '@/components/UserAvatar'

type SPost = { id: string; body: string; userName: string | null; source: string | null; createdAt: string | null; userImage?: string | null; donorTier?: string | null; level?: number | null }

// Render @username tokens as violet links to that user's profile (the tag also fires a notification,
// server-side, when the post is created). Plain text otherwise; keeps whitespace via the parent <p>.
function renderBody(body: string): React.ReactNode {
  const parts = body.split(/(@[a-zA-Z0-9_]{2,30})/g)
  return parts.map((part, i) => {
    if (/^@[a-zA-Z0-9_]{2,30}$/.test(part)) {
      const name = part.slice(1)
      return (
        <Link key={i} href={`/profile/${name}`} className="font-semibold text-violet-600 dark:text-violet-300 hover:text-violet-700 dark:hover:text-violet-200 hover:underline">
          {part}
        </Link>
      )
    }
    return part
  })
}

function timeAgo(ts: string | number | Date | null | undefined): string {
  if (ts == null) return ''
  // createdAt can arrive as a string (JSON poll), a Date (SSR props cross the RSC boundary as
  // real Dates), or a number (epoch) — coerce all three. Naive UTC strings get a 'Z' so they're
  // parsed as UTC, not local. Guards against "ts.endsWith is not a function" on non-string values.
  let d: Date
  if (ts instanceof Date) d = ts
  else if (typeof ts === 'number') d = new Date(ts)
  else {
    const s = String(ts)
    d = new Date(s.endsWith('Z') || s.includes('+') ? s : `${s}Z`)
  }
  if (Number.isNaN(d.getTime())) return ''
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000)
  if (s < 60) return 'now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })
}

export default function SymbolFeed({ symbol, initialPosts }: { symbol: string; initialPosts: SPost[] }) {
  const { data: session } = useSession()
  const [posts, setPosts] = useState<SPost[]>(initialPosts)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newIds, setNewIds] = useState<Set<string>>(new Set())
  const knownIds = useRef<Set<string>>(new Set(initialPosts.map((p) => p.id)))
  const uname = session?.user?.username
  const isAdmin = isAdminSession(session)

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`/api/stocks/posts?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      if (!r.ok) return
      const j = await r.json()
      if (!Array.isArray(j?.posts)) return
      const incoming: SPost[] = j.posts
      const fresh = incoming.filter((p) => !knownIds.current.has(p.id)).map((p) => p.id)
      for (const id of fresh) knownIds.current.add(id)
      setPosts(incoming)
      if (fresh.length) {
        setNewIds((prev) => new Set([...prev, ...fresh]))
        setTimeout(() => setNewIds((prev) => {
          const n = new Set(prev)
          for (const id of fresh) n.delete(id)
          return n
        }), 2500)
      }
    } catch { /* keep current */ }
  }, [symbol])

  // live stream — the conversation moves while you watch
  useEffect(() => {
    const iv = setInterval(() => { if (!document.hidden) refresh() }, 12_000)
    return () => clearInterval(iv)
  }, [refresh])

  const submit = async () => {
    if (busy || !body.trim()) return
    setBusy(true); setError(null)
    try {
      const r = await fetch('/api/stocks/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, body: body.trim() }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j?.error || `Failed (${r.status})`)
      setBody('')
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post, try again.')
    } finally {
      setBusy(false)
    }
  }

  const hide = async (id: string) => {
    try { await fetch(`/api/stocks/posts?id=${encodeURIComponent(id)}`, { method: 'DELETE' }); await refresh() } catch { /* noop */ }
  }

  return (
    <div className="space-y-3">
      {/* ── Composer ── */}
      {session?.user ? (
        <div className="rounded-xl border border-border bg-card p-3 transition-colors focus-within:border-amber-600/50">
          <div className="flex gap-2.5">
            <UserAvatar src={session.user.image} name={uname ?? 'you'} size={32}
              donorTier={(session.user as { donorTier?: string | null }).donorTier}
              level={(session.user as { level?: number | null }).level} />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
              rows={2}
              placeholder={`What's your take on $${symbol}?`}
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-amber-600/60 focus:outline-none"
            />
          </div>
          {error && <p className="mt-1 pl-[42px] text-xs text-rose-600 dark:text-rose-400">{error}</p>}
          <div className="mt-2 flex items-center justify-between pl-[42px]">
            <span className="text-[11px] text-muted-foreground">Enter to post · Shift+Enter for a new line</span>
            <button
              onClick={submit}
              disabled={busy || !body.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-black transition-transform active:scale-95 disabled:opacity-40"
              style={{ background: busy ? '#92400e' : 'linear-gradient(135deg,#f59e0b,#fb923c)' }}
            >
              <Send className="h-3 w-3" /> {busy ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-border bg-card p-3 text-sm text-muted-foreground">
          <a href="/auth" className="font-semibold text-amber-600 dark:text-amber-300 hover:text-amber-700 dark:hover:text-amber-200">Sign in</a> to join the ${symbol} conversation. It&apos;s free.
        </p>
      )}

      {/* ── Live stream (polls every 12s; new posts ring in) ── */}
      {posts.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-4 py-8 text-center">
          <Radio className="mx-auto mb-2 h-6 w-6 text-amber-600/60 dark:text-amber-400/60" />
          <p className="text-sm font-semibold text-foreground">Nobody&apos;s called ${symbol} yet.</p>
          <p className="mt-1 text-xs text-muted-foreground">The first take sets the tone. What are you seeing?</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => {
            const name = p.userName ?? 'trader'
            const isWatch = p.source === 'watchlist'
            return (
              <li key={p.id}
                className={`rounded-xl border px-3.5 py-2.5 transition-all duration-700 ${
                  newIds.has(p.id) ? 'border-amber-500/50 ring-1 ring-amber-400/30'
                    : isWatch ? 'border-emerald-500/20 bg-emerald-500/[0.04]' : 'border-border bg-card'
                }`}>
                <div className="flex items-start gap-2.5">
                  <UserAvatar src={p.userImage} name={name} size={28} donorTier={p.donorTier} level={p.level} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Link href={`/profile/${encodeURIComponent(name)}`} className="font-bold text-foreground hover:text-amber-600 dark:hover:text-amber-300">{name}</Link>
                      <span className="text-muted-foreground">{timeAgo(p.createdAt)}</span>
                      {p.source === 'chat' && (
                        <span className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400/90">live chat</span>
                      )}
                      {isWatch && (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400/90"><Eye className="h-2.5 w-2.5" />watchlist</span>
                      )}
                      {isAdmin && (
                        <button onClick={() => hide(p.id)} className="ml-auto text-[10px] text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400">hide</button>
                      )}
                    </div>
                    {isWatch ? (
                      <p className="mt-0.5 text-sm leading-relaxed text-emerald-700 dark:text-emerald-200/80">Added <span className="font-semibold">${symbol}</span> to their watchlist</p>
                    ) : (
                      <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{renderBody(p.body)}</p>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
